/* eslint-disable no-restricted-properties */
/* eslint-disable fp/no-loops */
import { evaluate } from 'mathjs'

import { OperatorSignType } from '../types'

import { ExamineEquationType } from './types'

/**
 * Check if a character is digit or not, according to PEMDAS
 * @param char single char string
 * @returns boolean
 */
function isDigit(char: string): boolean {
  return /^\d$/.test(char)
}

/**
 * Reading equation and finding operators.
 * @param equation
 * @param indexNum
 * @returns
 */
function examineEquation(equation: string, indexNum: number): ExamineEquationType {
  const result: ExamineEquationType = {
    hasPow: false,
    hasMulDiv: false,
    status: true,
  }

  const setPresence = (operator: string): void => {
    if (operator === 's' || operator === '^') {
      result.hasPow = true
    } else if (operator === '/' || operator === '*') {
      result.hasMulDiv = true
    }
  }

  for (let i = indexNum - 1; i >= 0 && result.status; i--) {
    if (equation[i] === '(') break
    if (equation[i] === ')') {
      // Means some of the parenthesis isn't solved yet.
      result.status = false
    }
    setPresence(equation[i])
  }

  for (let i = indexNum + 1; i < equation.length && result.status; i++) {
    if (equation[i] === ')') break
    if (equation[i] === '(') {
      // Means some of the parenthesis isn't solved yet.
      result.status = false
    }
    setPresence(equation[i])
  }
  return result
}

/**
 * Deciding the precedence of an Operator
 * @param data
 * @param operatorType
 * @returns
 */
function decidePrecedence(data: ExamineEquationType, operatorType: OperatorSignType): boolean {
  // check If there any [/, *, ^, √] left, while trying to do [+, & -]
  if (
    (operatorType === OperatorSignType.ADDITION || operatorType === OperatorSignType.SUBTRACTION) &&
    (data.hasMulDiv || data.hasPow)
  ) {
    return false
  }

  // Check if there any [^, √], while trying to do [/, *]
  if ((operatorType === OperatorSignType.DIVISION || operatorType === OperatorSignType.MULTIPLICATION) && data.hasPow) {
    return false
  }

  return true
}

/**
 * Determine if a operator is performable or not.
 * @param equation
 * @param indexNum
 * @param operatorType
 * @returns
 */
export function checkPossibility(equation: string, indexNum: number, operatorType: OperatorSignType): boolean {
  try {
    // If there is any sign inside square root then it's not the right time to evaluate. sqrt(1+2)
    if (operatorType === OperatorSignType.SQUARE_ROOT) {
      let end = indexNum
      while (end < equation.length && equation[end] !== ')') end++
      const data = equation.substring(indexNum, end + 1).split('') ?? []
      const operators = ['+', '-', '/', '*', '^']
      return !data.some((r) => operators.includes(r))
    }

    // If there is a parenthesis then there must be some equation, (2+3)^3
    if (operatorType === OperatorSignType.POWER) {
      return indexNum > 0 && equation[indexNum - 1] !== ')'
    }

    const result = examineEquation(equation, indexNum)
    if (!result.status) return false
    return decidePrecedence(result, operatorType)
  } catch (err) {
    return false
  }
}

/**
 * Perform an Operation, AKA solve a single term
 * @param equation
 * @param indexNum
 * @param operatorType
 * @returns
 */
export function solveTerm(equation: string, indexNum: number, operatorType: OperatorSignType): string {
  try {
    let st = indexNum
    let end = indexNum
    let solve = 0
    if (operatorType === OperatorSignType.SQUARE_ROOT) {
      while (end < equation.length && equation[end] !== ')') end++
      solve = evaluate(equation.substring(indexNum, end + 1))
    } else {
      st = indexNum - 1
      end = indexNum + 1
      while (st - 1 >= 0 && isDigit(equation[st - 1])) st--
      if (st - 1 >= 0 && (equation[st - 1] === '-' || equation[st - 1] === '+')) st--
      while (end + 1 < equation.length && isDigit(equation[end + 1])) end++
      solve = evaluate(equation.substring(st, end + 1))
    }

    // Cutting parenthesis, Example: a+(b+c) after solving (b+c) => a+d
    if (st - 1 >= 0 && end + 1 < equation.length && equation[st - 1] === '(' && equation[end + 1] === ')') {
      st--
      end++
    }

    // Considering sign before parenthesis, Example: a-(b+c) after solving (b+c) => a-d
    if (st - 1 >= 0 && (equation[st - 1] === '-' || equation[st - 1] === '+')) {
      if (equation[st - 1] === '-') solve *= -1
      st--
    }

    // For Square Root bracket is needed so undoing last cut.
    if (st - 1 >= 0 && equation[st - 1] === 't') {
      // UnDoing parenthesis cut
      st++
      end--
    }

    let firstPart = equation.substring(0, st) ?? ''
    const secondPart = equation.substring(end + 1) ?? ''

    // Include + sign when needed.
    if (solve >= 0 && firstPart.length > 0 && isDigit(firstPart[firstPart.length - 1])) {
      firstPart += '+'
    }

    return `${firstPart}${solve}${secondPart}`
  } catch (err) {
    return equation
  }
}
